/**
 * LinksPanel — V86t · JL-3 (two tabs).
 *
 * Standard §13 deployment registry, served by the daemon at /links
 * (reads + watches `.meshkore/public/links.yaml`, broadcasts
 * `links.updated` over WS). Operator's mental model:
 *   - PROD is the canonical URL of a module — that's the link they
 *     actually click to verify the deploy. Sits big and primary.
 *   - LOCAL is the dev URL + the command the operator (or their code
 *     agent) runs to spin up that module locally. Cockpit never
 *     spawns; we just SHOW the command so the operator can copy/paste.
 *   - REPO is the branch + commit currently checked out, surfaced so
 *     the operator can tell at a glance whether the prod deploy is in
 *     sync with HEAD.
 *
 * The panel listens for `links.updated` so changes to links.yaml
 * (operator typing, code agent writing) show up without refresh.
 *
 * JL-3 — Bookmarks folded in as the SECOND tab of this zone. It used to
 * be its own header button pointing at a "coming with V44" stub that
 * never shipped: two buttons for almost the same thing, one of them
 * empty. The two tabs are deliberately different in kind:
 *   - Internal: operational deployment registry (ports, prod URLs, run
 *     commands) — one entry per module, standard §13.
 *   - Bookmarks: curated reading — docs, dashboards, the standard —
 *     served from `bookmarks:` in the SAME links.yaml (JL-4), so there
 *     is one file, one registry and one `links.updated` event.
 */

import { For, Show, createEffect, createMemo, createResource, createSignal, onCleanup } from 'solid-js';
import { daemonStore } from '~/state/daemon';
import { allModules, clusterInfo } from '~/state/server';
import { uiStore, type LinksTab } from '~/state/ui';
import { log } from '~/lib/log';
import { CopyButton } from '~/components/ui/CopyButton';
import type { LinkBookmark, LinksModule, LinksRegistry } from '~/lib/daemon-client';

export default function LinksPanel() {
  const [reloadKey, setReloadKey] = createSignal(0);

  const [registry] = createResource(
    () => ({ client: daemonStore.state.client, key: reloadKey() }),
    async (input) => {
      if (!input.client) throw new Error('no daemon client');
      const r = await input.client.links();
      if (!r.ok) {
        throw new Error(r.error ?? `HTTP ${r.status}: ${r.body.slice(0, 200)}`);
      }
      return r.data as LinksRegistry;
    },
  );

  // V86t — subscribe to `links.updated` so edits to links.yaml propagate
  // without a refresh. The daemon's LinksRegistry broadcasts on every
  // file mtime tick; we just bump our reload-key signal.
  // V107.2 — use createEffect (not onMount) so the subscription re-binds
  // to the NEW ws after a project swap. With onMount the panel was
  // listening to the dead WS of the previously-active cluster.
  createEffect(() => {
    const ws = daemonStore.state.ws;
    if (!ws) return;
    const off = ws.on('links.updated', () => {
      log.debug('[LinksPanel] links.updated — refetch');
      setReloadKey((k) => k + 1);
    });
    onCleanup(off);
  });

  const modules = createMemo<LinksModule[]>(() => registry()?.modules ?? []);
  const bookmarks = createMemo<LinkBookmark[]>(() => registry()?.bookmarks ?? []);
  const tab = () => uiStore.state.linksTab;
  const count = () => (tab() === 'internal' ? modules().length : bookmarks().length);
  const unit = () => (tab() === 'internal' ? 'module' : 'bookmark');

  return (
    <div class="flex-1 min-h-0 flex flex-col">
      <header class="px-6 pt-6 pb-3 border-b border-gray-800/60 flex items-center gap-3 flex-shrink-0">
        <div class="w-8 h-8 rounded-lg bg-sky-500/15 border border-sky-500/30 flex items-center justify-center">
          <svg class="w-4 h-4 text-sky-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
          </svg>
        </div>
        <div>
          <h1 class="text-base font-semibold text-gray-100">Links</h1>
          <p class="text-xs text-gray-500">
            {tab() === 'internal' ? 'Deployment registry' : 'Curated project reading'} ·{' '}
            <code class="font-mono text-sky-300/80">.meshkore/public/links.yaml</code>
          </p>
        </div>
        <div class="ml-auto flex items-center gap-2">
          <div class="flex items-center gap-1 mr-1" role="tablist" aria-label="Links tabs">
            <TabButton id="internal" label="Internal" active={tab()} />
            <TabButton id="bookmarks" label="Bookmarks" active={tab()} />
          </div>
          <Show when={count() > 0}>
            <span class="text-[10px] font-mono uppercase tracking-wider text-gray-500">
              {count()} {unit()}{count() === 1 ? '' : 's'}
            </span>
          </Show>
          <button
            type="button"
            onClick={() => uiStore.setActiveZone('architect')}
            class="px-2.5 py-1 rounded-md bg-gray-800/60 hover:bg-gray-700/60 text-gray-300 text-[11px] font-mono uppercase tracking-wider transition-colors"
          >
            ← back
          </button>
        </div>
      </header>

      <div class="flex-1 min-h-0 overflow-y-auto px-6 py-6">
        <div class="max-w-3xl mx-auto">
          <Show when={registry.error}>
            <ErrorNotice error={String(registry.error)} />
          </Show>
          <Show when={registry.loading && !registry()}>
            <p class="text-sm text-gray-500">Loading links…</p>
          </Show>

          {/* V107.18 — when the registry is empty AND the cluster has
              declared modules in cluster.yaml, the EmptyNotice's
              hardcoded `meshkore-web` example was misleading on every
              other project. Now the example is rendered from
              `allModules()` so cavioca sees `web/api/builder/...`,
              not `meshkore-web`. Falls back to a generic `<your-module>`
              placeholder only when the cluster has no modules either. */}

          <Show when={tab() === 'internal'}>
            <Show when={!registry.loading && modules().length === 0 && !registry.error}>
              <EmptyNotice />
            </Show>
            <ul class="grid grid-cols-1 md:grid-cols-2 gap-2.5">
              <For each={modules()}>
                {(m) => <ModuleCard mod={m} />}
              </For>
            </ul>
          </Show>

          <Show when={tab() === 'bookmarks'}>
            <Show when={!registry.loading && bookmarks().length === 0 && !registry.error}>
              <BookmarksEmptyNotice />
            </Show>
            <ul class="grid grid-cols-1 md:grid-cols-2 gap-2.5">
              <For each={bookmarks()}>
                {(b) => <BookmarkCard bookmark={b} />}
              </For>
            </ul>
          </Show>
        </div>
      </div>
    </div>
  );
}

function TabButton(props: { id: LinksTab; label: string; active: LinksTab }) {
  const on = () => props.active === props.id;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={on()}
      onClick={() => uiStore.setLinksTab(props.id)}
      class={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors ${
        on()
          ? 'bg-sky-500/15 border-sky-500/40 text-sky-200'
          : 'border-transparent text-gray-500 hover:text-gray-200 hover:border-gray-700'
      }`}
    >
      {props.label}
    </button>
  );
}

/** One curated bookmark. Flat on purpose — a bookmark is a title, a
 *  destination and (optionally) why it is worth opening. */
function BookmarkCard(props: { bookmark: LinkBookmark }) {
  const href = () => props.bookmark.url?.trim() || null;
  return (
    <li class="rounded-lg border border-gray-800/60 bg-gray-900/30 px-3 py-2.5 flex flex-col gap-1.5">
      <header class="flex items-center gap-2 min-w-0">
        <span class="text-[12px] font-medium text-gray-100 truncate">
          {props.bookmark.title || href()}
        </span>
        <For each={props.bookmark.tags ?? []}>
          {(t) => (
            <span class="font-mono text-[9px] text-gray-500 uppercase tracking-wider shrink-0">{t}</span>
          )}
        </For>
      </header>

      <Show when={href()}>
        <a
          href={href()!}
          target="_blank"
          rel="noopener noreferrer"
          class="block px-2 py-1 rounded bg-sky-500/10 hover:bg-sky-500/20 border border-sky-500/30 text-sky-200 font-mono text-[11px] truncate transition-colors"
          title={href()!}
        >
          ↗ {href()}
        </a>
      </Show>

      <Show when={props.bookmark.notes}>
        <p class="text-[11px] text-gray-500 leading-snug">{props.bookmark.notes}</p>
      </Show>
    </li>
  );
}

function BookmarksEmptyNotice() {
  return (
    <div class="rounded-lg border border-gray-800/70 bg-gray-900/40 px-5 py-6 text-sm text-gray-400">
      <p class="mb-2">No bookmarks yet.</p>
      <p class="text-[12px] text-gray-500 mb-3">
        Bookmarks are the project's curated reading — docs, dashboards, the standard — as opposed to
        the operational URLs on the <strong class="text-gray-400">Internal</strong> tab. They live in
        the same <code class="font-mono text-sky-300/80">.meshkore/public/links.yaml</code>, under a
        top-level <code class="font-mono text-sky-300/80">bookmarks:</code> block:
      </p>
      <pre class="text-[11px] font-mono text-gray-300 bg-gray-950/70 border border-gray-800/70 rounded-md px-3 py-2 whitespace-pre-wrap">{`bookmarks:
  - title: The MeshKore standard
    url: https://meshkore.com/standard
    notes: The cross-cluster contract
  - title: Cloudflare dashboard
    url: https://dash.cloudflare.com
    tags: [ops]`}</pre>
    </div>
  );
}

function ModuleCard(props: { mod: LinksModule }) {
  // V86v — compact card. Operator's feedback: tarjetas a la mitad,
  // menos contenido, más al grano. Dropped fields: displayName,
  // project (we know which project we're in), region, deploy_command
  // (deploy agent handles it), deployed sha/ts/by (timeline tracks
  // that), repo footer, health endpoint, out-of-sync banner.
  //
  // What stays: module id (header chip), prod URL pill + optional
  // provider tag, local URL pill + optional run command. The card
  // collapses to whatever it has — empty slots render nothing.
  const prodHref = () => props.mod.prod?.url?.trim() || null;
  const localHref = () => props.mod.local?.url?.trim() || null;
  const localCmd = () => props.mod.local?.command?.trim() || null;
  const provider = () => props.mod.prod?.provider?.trim() || null;

  return (
    <li class="rounded-lg border border-gray-800/60 bg-gray-900/30 px-3 py-2.5 flex flex-col gap-1.5">
      <header class="flex items-center gap-2">
        <span class="font-mono text-[10px] text-sky-300/90 bg-sky-500/10 border border-sky-500/25 rounded px-1.5 py-0.5 uppercase tracking-wider flex-shrink-0">
          {props.mod.id}
        </span>
        <Show when={provider()}>
          <span class="font-mono text-[9px] text-gray-500 uppercase tracking-wider">
            {provider()}
          </span>
        </Show>
      </header>

      <Show when={prodHref()}>
        <a
          href={prodHref()!}
          target="_blank"
          rel="noopener noreferrer"
          class="block px-2 py-1 rounded bg-sky-500/10 hover:bg-sky-500/20 border border-sky-500/30 text-sky-200 font-mono text-[11px] truncate transition-colors"
          title={prodHref()!}
        >
          ↗ {prodHref()}
        </a>
      </Show>

      <Show when={localHref()}>
        <a
          href={localHref()!}
          target="_blank"
          rel="noopener noreferrer"
          class="block px-2 py-1 rounded bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/25 text-emerald-200 font-mono text-[11px] truncate transition-colors"
          title={localHref()!}
        >
          ⌂ {localHref()}
        </a>
      </Show>

      <Show when={localCmd()}>
        <CommandBlock cmd={localCmd()!} />
      </Show>

      <Show when={!prodHref() && !localHref() && !localCmd()}>
        <p class="text-[11px] text-gray-600 italic">No links yet.</p>
      </Show>
    </li>
  );
}

function CommandBlock(props: { cmd: string }) {
  return (
    <div class="flex items-center gap-2 rounded border border-gray-800/60 bg-gray-950/60 px-2 py-1">
      <code class="flex-1 min-w-0 text-[10px] font-mono text-gray-400 truncate" title={props.cmd}>
        {props.cmd}
      </code>
      <CopyButton
        text={() => props.cmd}
        copiedLabel="copied"
        class="text-[9px] font-mono uppercase tracking-wider text-gray-500 hover:text-emerald-300 flex-shrink-0"
      />
    </div>
  );
}

function ErrorNotice(props: { error: string }) {
  return (
    <div class="rounded-lg border border-red-500/30 bg-red-500/10 px-5 py-4 text-sm text-red-200 mb-4">
      <p class="font-semibold mb-1">Couldn't load the links registry.</p>
      <p class="text-red-300/80 font-mono text-[12px]">{props.error}</p>
    </div>
  );
}

function EmptyNotice() {
  // V107.18 — example now uses the actual cluster's module ids
  // (read from cluster.yaml via serverStore). Old code hardcoded
  // `meshkore-web` / `meshkore.com` which was correct only for the
  // MeshKore repo itself and misled every other project.
  const example = createMemo(() => {
    const codeModules = allModules().filter((m) => m.kind !== 'area');
    const first = codeModules[0];
    const clusterId = clusterInfo()?.id ?? 'your-cluster';
    if (!first) {
      return `version: 1
modules:
  - id: <your-module>          # match an id from cluster.yaml
    prod:
      url: https://<your-domain>
      provider: cloudflare-pages
      project: ${clusterId}-<module>
    local:
      url: http://localhost:8788
      command: <how you run it locally>
    repo:
      branch: main`;
    }
    return `version: 1
modules:
  - id: ${first.id}
    prod:
      url: https://<your-domain>
      provider: cloudflare-pages
      project: ${clusterId}-${first.id}
    local:
      url: http://localhost:8788
      command: <how you run it locally>
    repo:
      branch: main`;
  });

  const declared = createMemo(() =>
    allModules().filter((m) => m.kind !== 'area').map((m) => m.id),
  );

  return (
    <div class="rounded-lg border border-gray-800/70 bg-gray-900/40 px-5 py-6 text-sm text-gray-400">
      <p class="mb-2">No links declared yet.</p>
      <p class="text-[12px] text-gray-500 mb-3">
        Add a <code class="font-mono text-sky-300/80">.meshkore/public/links.yaml</code> with one block per module. Minimal example
        <Show when={declared().length > 0}>
          {' '}— prefilled with this cluster's modules
        </Show>:
      </p>
      <pre class="text-[11px] font-mono text-gray-300 bg-gray-950/70 border border-gray-800/70 rounded-md px-3 py-2 whitespace-pre-wrap">{example()}</pre>
      <Show when={declared().length > 0}>
        <p class="text-[11px] text-gray-500 mt-3">
          Modules declared in <code class="font-mono text-sky-300/80">cluster.yaml</code>:{' '}
          <For each={declared()}>
            {(id, idx) => (
              <>
                <code class="font-mono text-sky-300/90">{id}</code>
                <Show when={idx() < declared().length - 1}>{', '}</Show>
              </>
            )}
          </For>
          . Add a block per module you want to surface here.
        </p>
      </Show>
    </div>
  );
}

